import { CoreUtils } from "../core";
import Big from "big.js";
import accounting from "accounting";
import {FORMATS} from "../core/formats";
import moment, {Moment} from "moment-timezone";
import cloneDeep from "lodash/cloneDeep";

interface HoursIsTimeWithinArgs {
  dt: Moment;
  timezone: string;
  hours: BusinessHours;
  special_hours?: BusinessSpecialHours;
  first_offset: number;
  last_offset: number;
}

interface HoursClosesInArgs {
  dt: Moment;
  timezone: string;
  hours: BusinessHours;
  special_hours?: BusinessSpecialHours;
  first_offset: number;
  last_offset: number;
}

export const RestaurantUtils = {
  opening_hours: {

    getOpenDays(hours: BusinessHours): DaysOfWeek[] {
      return hours.map(({day}) => day);
    },

    isTimeWithin({ dt, hours, special_hours, first_offset, last_offset, timezone }: HoursIsTimeWithinArgs) {

      let matchIndex = -1;
      let isWithin = false;

      const weekDate = dt.format(FORMATS.moment.date);
      const weekday = dt.clone().locale("en").format("dddd");

      const filtered_special_hours = (special_hours || [])
        .map((h, i) => ({ ...h, index: i }))
        .filter((h) => h.date === weekDate);

      const isSpecialHours = filtered_special_hours.length > 0;

      const opening_hours = isSpecialHours ? filtered_special_hours : hours
        .map((h, i) => ({ ...h, index: i }))
        .filter((h) => h.day === weekday);

      for (const slot of opening_hours) {

        // @ts-ignore
        if (isSpecialHours && slot.opened === false) {
          continue;
        }

        if (slot.h24) {
          isWithin = true;
          matchIndex = slot.index;
          break;
        }

        const date = dt.format(FORMATS.moment.date);
        const open = moment.tz(`${date} ${slot.open}`, FORMATS.moment.datetime, timezone).add(first_offset, "minutes");
        const close = moment.tz(`${date} ${slot.close}`, FORMATS.moment.datetime, timezone).subtract(last_offset, "minutes");

        if (dt.isAfter(open) && dt.isBefore(close)) { // IF CURRENT BETWEEN OPEN AND CLOSE
          isWithin = true;
          matchIndex = slot.index;
          break;
        }

      }

      return { isWithin, isSpecialHours, index: matchIndex };

    },

    closesIn({ dt, hours, special_hours, first_offset, last_offset, timezone }: HoursClosesInArgs): { minutes: number; millis: number } | null {

      const { isWithin, index, isSpecialHours } = this.isTimeWithin({ dt, hours, special_hours, first_offset, last_offset, timezone });

      if (!isWithin)
        return null;

      const slot = isSpecialHours ? special_hours![index] : hours[index];

      let continuesToNextDay = false;
      let nextSlot = null;
      if (slot.close === "23:59" || slot.h24) {
        const nextWeekday = CoreUtils.dates.getNextWeekday(slot.day);
        const nextDayTimes = hours.filter((s) => s.day === nextWeekday);
        for (const s of nextDayTimes) {
          if (s.h24 || s.open === "00:00") {
            continuesToNextDay = true;
            nextSlot = s;
            break;
          }
        }
      }

      const parsed = moment.tz(`${dt.format(FORMATS.moment.date)} ${slot.close}`, FORMATS.moment.datetime, timezone).subtract(last_offset, "minutes");
      if (continuesToNextDay && nextSlot) {
        parsed.add("day", 1);
        parsed.set("hour", parseInt(nextSlot.close.split(":")[0], 10));
        parsed.set("minute", parseInt(nextSlot.close.split(":")[1], 10));
        parsed.subtract(last_offset, "minutes");
      }

      console.log("CLOSES AT", parsed.format(FORMATS.moment.datetime));

      const closesInMinutes = Math.round(parsed.diff(dt, "minutes"));
      const closesInMillis = Math.round(parsed.diff(dt, "milliseconds"));

      return (closesInMinutes >= 0 && closesInMinutes <= 30) ? {
        minutes: closesInMinutes,
        millis: closesInMillis,
      } : null;

    },

  },
  theme: {
    fromRestaurant(r: RestaurantSafeSchema): ThemeInterface {
      const { fonts, colors, sections } = r.website;
      return {
        font: {
          header: `'${fonts.heading.family}', ${fonts.heading.category};`,
          body: `'${fonts.body.family}',Verdana,Helvetica,${fonts.body.category};`,
          size: 16,
        },
        colors: {
          primary: colors.primary,
          primary_text: colors.primary_text,
          text: colors.text,
          background: colors.background,
          success: "#51a351",
          warning: "#f89406",
          error: "#bd362f",
        },
        status_colors: {
          complete: "#51A351",
          onroute: "#00BFFF",
          ready: "#0080FF",
          confirmed: "#B7D024",
          unconfirmed: "#efbb1e",
          cancelled: "#BD362F",
        },
        button: {
          height: 38,
        },
        box: {
          background: colors.box,
          text: colors.box_text,
          border: CoreUtils.colors.shadeAlternate(0.16, colors.box),
        },
        input: {
          background: CoreUtils.colors.shadeAlternate(0.02, colors.box),
          text: CoreUtils.colors.shadeAlternate(0.02, colors.box_text),
          border: CoreUtils.colors.shadeAlternate(0.2, colors.box),
          height: 38,
          fontSize: 16,
        },
        top_nav: {
          zindex: 10,
          height: 70,
          background_color: sections.top_nav.colors.background,
          text_color: sections.top_nav.colors.text,
        },
        side_nav: {
          zindex: 9,
          width: 70,
          background_color: colors.box,
          text_color: colors.box_text,
        },
        slide_panel: {
          zindex: 9,
          background_color: colors.box,
          text_color: colors.box_text,
        },
        footer: {
          background: sections.footer.colors.background,
          text: sections.footer.colors.text,
        },
        content_width: {
          xs: 360,
          sm: 480,
          md: 720,
          lg: 1140,
          xl: 1360,
        },
        breakpoints: CoreUtils.ui.breakpoints,
      };
    },
  },
  menu: {
    getTree(r: RestaurantSchema) {
      return r.menus.filter((m) => m.categories.length).map((menu) => ({
        value: menu._id,
        label: menu.name,
        children: menu.categories.filter((c) => c.dishes.length).map((category) => ({
          value: category._id,
          label: category.name,
          children: category.dishes.map((dish) => ({
            value: dish._id,
            label: dish.name,
          })),
        })),
      })).filter((node) => node.children.length);
    },
    getTreeFilter(r: RestaurantSchema, filter: (dish: RestaurantDish) => boolean) {
      return r.menus.filter((m) => m.categories.length).map((menu) => ({
        value: menu._id,
        label: menu.name,
        children: menu.categories.filter((c) => c.dishes.length).map((category) => ({
          value: category._id,
          label: category.name,
          children: category.dishes.filter(filter).map((dish) => ({
            value: dish._id,
            label: dish.name,
          })),
        })),
      })).filter((node) => node.children.length);
    },
    getCategories(r: RestaurantSafeSchema) {
      const categories = [];
      for (const menu of r.menus) {
        for (const category of menu.categories) {
          categories.push(category);
        }
      }
      return categories;
    },
    getDishes(r: RestaurantSafeSchema) {
      const dishes = [];
      for (const menu of r.menus) {
        for (const category of menu.categories) {
          for (const dish of category.dishes) {
            dishes.push(dish);
          }
        }
      }
      return dishes;
    },
    findCategoryById(r: RestaurantSafeSchema, category_id: string) {
      for (let menuIndex = 0; menuIndex < r.menus.length; menuIndex++) {
        const menu = r.menus[menuIndex];
        for (let categoryIndex = 0; categoryIndex < menu.categories.length; categoryIndex++) {
          const category = menu.categories[categoryIndex];
          if (category._id === category_id) {
            return  { menu, category, menuIndex, categoryIndex };
          }
        }
      }
      return null;
    },
    findDish(r: RestaurantSafeSchema, dish_id: string): null | { dish: RestaurantDish, category: RestaurantCategory, menu: RestaurantMenu } {
      for (const menu of r.menus) {
        for (const category of menu.categories) {
          for (const dish of category.dishes) {
            if (dish._id === dish_id) {
              return { dish, category, menu };
            }
          }
        }
      }
      return null;
    },
    findDishCategory(r: RestaurantSafeSchema, dish_id: string) {
      for (const menu of r.menus) {
        for (const category of menu.categories) {
          for (const dish of category.dishes) {
            if (dish._id === dish_id) {
              return category;
            }
          }
        }
      }
      return null;
    },
    filterDishes(r: RestaurantSafeSchema, filter: (item: RestaurantDish) => boolean) {
      const dishes = [];
      for (const menu of r.menus) {
        for (const category of menu.categories) {
          for (const dish of category.dishes) {
            if (filter(dish)) {
              dishes.push(dish);
            }
          }
        }
      }
      return dishes;
    },
  },
  dish: {
    calculateTotal(dish: OrderDish) {

      let price = Big(dish.price);
      let modified = Big(0);

      if (dish.type === "combo") {
        // CALCULATE MODIFIER TOTALS AND DIFFERENCE PRICES FOR EACH CHOICE
        const choice_difference_totals: string[] = [];
        const option_set_totals: string[] = [];

        for (const choice of dish.choices) {
          if (choice.selected) {
            const choiceDish = choice.selected;
            if (choiceDish.option_sets && choiceDish.option_sets.length > 0) {
              const { total } = RestaurantUtils.dish.optionSetTotal(choiceDish.option_sets);
              option_set_totals.push(total);
            }
            if (dish.price_type === "difference") {
              const lowest_price_option = choice.lpo;
              if (Big(choiceDish.price).gt(lowest_price_option)) {
                choice_difference_totals.push(Big(choiceDish.price).minus(lowest_price_option).toString());
              }
            }
          }
        }

        const option_set_total = option_set_totals.reduce((total, modifier) => Big(total).plus(modifier), Big(0));
        const choice_difference_total = choice_difference_totals.reduce((total, modifier) => Big(total).plus(modifier), Big(0));

        modified = modified.plus(option_set_total).plus(choice_difference_total);

        price = price.plus(option_set_total);
        price = price.plus(choice_difference_total);

      }
      else {
        const { total } = RestaurantUtils.dish.optionSetTotal(dish.option_sets);
        modified = modified.plus(total);
        price = price.plus(total);
      }

      price = price.times(dish.qty);

      return {
        price: price.toFixed(),
        modified: modified.toFixed(),
      };

    },
    optionSetTotal(option_sets: OrderDish["option_sets"]) {

      let total = Big(0);

      const freeTillIndexs = [];

      for (let i = 0; i < option_sets.length; i++) {
        const option_set = option_sets[i];

        const { conditions } = option_set;
        const freeQty = conditions.free_amount;
        let freeUsed = 0;

        for (let index = 0; index < option_set.options.length; index++) {
          const option = option_set.options[index];
          if (option.quantity > 0) {
            for (let y = 0; y < option.quantity; y++) {
              if (conditions.multi_select && freeQty && freeUsed < freeQty) {
                freeUsed++;
              }
              else {
                if (freeTillIndexs[i] === undefined && freeQty) {
                  freeTillIndexs[i] = index;
                }
                total = Big(total).plus(option.price || 0);
              }
            }
          }
        }
      }

      return {
        total: total.toString(),
        freeTillIndexs: freeTillIndexs,
      };
    },
    optionSetValidate(option_sets: OrderDish["option_sets"]) {
      const errors = [];
      for (const set of option_sets) {
        let ok = false;
        const conditions = set.conditions;
        const total = set.options.reduce((t, o) => t + o.quantity, 0);
        if (conditions.required) {
          if (conditions.multi_select) {
            if (conditions.max_options) {
              if ((conditions.min_options || 1) <= total && total <= conditions.max_options) {
                ok = true;
              }
            }
            else if (total >= (conditions.min_options || 1)) {
              ok = true;
            }
          }
          else if (total === 1) {
            ok = true;
          }
        }
        // tslint:disable-next-line
        else {
          if (conditions.max_options) {
            if (total <= conditions.max_options) {
              ok = true;
            }
          }
          else {
            ok = true;
          }
        }
        errors.push(!ok);
      }
      return errors;
    },
  },
  cart: {

  },
  trim: {
    option_sets(option_sets: RestaurantOptionSet[]) {
      if (option_sets && option_sets.length > 0) {
        for (const [k, os] of option_sets.entries()) {
          delete option_sets[k].show_in_menu;
          let option_length = os.options.length;
          while (option_length--) {
            const optionQty = os.options[option_length].quantity;
            if (!Number.isInteger(optionQty) || optionQty === 0) {
              option_sets[k].options.splice(option_length, 1);
            }
          }
        }
      }
    },
    dish(dish: OrderDish) {
      delete dish.image;
      delete dish.description;
      if (dish.type === "combo") {
        for (let i = 0; i < dish.choices.length; i++) {
          this.option_sets(dish.choices[i].selected!.option_sets);
          delete dish.choices[i].dishes;
        }
      }
      else {
        this.option_sets(dish.option_sets);
      }
      return dish;
    },
  },
  untrim: {
    option_sets(dish: OrderDish, r: RestaurantSafeSchema): OrderDish {

      if (dish.option_sets && dish.option_sets.length > 0) {

        const sets: RestaurantOptionSet[] = cloneDeep(dish.option_sets.map((os) => r.option_sets.find((set) => set._id === os._id)!));

        for (const os of dish.option_sets) {
          const setIndex = sets.findIndex((set) => set._id === os._id);
          for (const o of os.options) {
            if (Number.isInteger(o.quantity) && o.quantity !== 0) {
              const index = sets[setIndex].options.findIndex((opt) => opt._id === o._id);
              sets[setIndex].options[index].quantity = o.quantity;
            }
          }
        }

        dish.option_sets = sets;

      }

      return dish;

    },
    dish(dish: OrderDish, r: RestaurantSafeSchema) {
      const current = RestaurantUtils.menu.findDish(r, dish._id);
      if (current) {
        dish.image = current.dish.image;
        dish.description = current.dish.description;
        if (dish.type === "combo") {
          for (let i = 0; i < dish.choices.length; i++) {
            if (dish.choices[i].selected) {
              this.option_sets(dish.choices[i].selected!, r);
              const currentChoice = current.dish.choices.find((c) => c._id === dish.choices[i]._id);
              dish.choices[i].dishes = cloneDeep(currentChoice!.dishes);
            }
          }
        }
        else {
          this.option_sets(dish, r);
        }
      }
      return dish;
    },
  },
  order: {
    color(status: OrderStatuses): string {
      switch (status) {
        case "unconfirmed":
          return "#efbb1e";
        case "confirmed":
          return "#B7D024";
        case "ready":
          return "#0080FF";
        case "on_route":
          return "#00BFFF";
        case "complete":
          return "#51A351";
        case "cancelled":
          return "#BD362F";
        default:
          return "#efbb1e";
      }
    },
    payment: {
      poli: {
        statusFromTransaction: (TransactionStatusCode: string): "success" | "error" | "pending" => {
          return (
            TransactionStatusCode === "Completed" ? "success" :
            TransactionStatusCode === "Cancelled" ? "error" :
            TransactionStatusCode === "Failed" ? "error" :
            TransactionStatusCode === "ReceiptUnverified" ? "error" :
            TransactionStatusCode === "TimedOut" ? "error" :
            "pending"
          );
        },
      },
      ipay88: {
        totalToAmount(total: number) {
          return accounting.formatMoney(total, "", 2, ",", ".");
        },
      },
    },
  },
  settings: {
    calculateEstimatedWaitTime(service: RestaurantServiceTypes, s: RestaurantService) {

      if (service === "delivery") {

        if (s.status.times.confirmed !== "" && s.status.times.ready !== "" && s.status.times.on_route !== "") {
          return (s.status.times.confirmed || 0) + (s.status.times.ready || 0) + (s.status.times.on_route || 0);
        }

        return null;

      }

      if (s.status.times.confirmed !== "" && s.status.times.ready !== "") {
        return (s.status.times.confirmed || 0) + (s.status.times.ready || 0);
      }

      return null;

    },
    defaultGuestMatchKeys: () => (["details.name", "details.phone", "details.email"]),
    defaultAudioNotifications(): RestaurantSettingsNotificationsAudio {
      return {
        sound: "job-done.ogg",
        repeat_count: 1,
        stop_on_click: false,
        play_on: {
          order_new: true,
          booking_new: true,
          customer_age_verification: true,
        },
      };
    },
    dateTimeFormat(region: RestaurantSchema["settings"]["region"]) {
      return `${region.formats.date} ${region.formats.time}`;
    },
    paymentActiveCash(payments: RestaurantSchema["settings"]["payments"]) {
      return !!(payments.cash && payments.cash.enabled);
    },
    paymentActiveCard(payments: RestaurantSchema["settings"]["payments"]) {
      return !!(payments.card && payments.card.enabled);
    },
    paymentActiveStripe(payments: RestaurantSchema["settings"]["payments"]) {
      const method = payments.stripe;
      return !!(
        method &&
        method.enabled &&
        method.secret_key &&
        method.publishable_key &&
        method.currency
      );
    },
    paymentActivePaypal(payments: RestaurantSchema["settings"]["payments"]) {
      const method = payments.paypal;
      return !!(
        method &&
        method.enabled &&
        method.client_id &&
        method.secret &&
        method.currency
      );
    },
    paymentActiveCardConnect(payments: RestaurantSchema["settings"]["payments"]) {
      const method = payments.cardconnect;
      return !!(
        method &&
        method.enabled &&
        method.site &&
        method.port &&
        method.username &&
        method.password &&
        method.merchid &&
        method.currency
      );
    },
    paymentActivePoliPay(payments: RestaurantSchema["settings"]["payments"]) {
      const method = payments.poli_pay;
      return !!(
        method &&
        method.enabled &&
        method.merchant_id &&
        method.auth_code &&
        method.currency
      );
    },
    paymentActivePaygatePayweb(payments: RestaurantSchema["settings"]["payments"]) {
      const method = payments.paygate_payweb;
      return !!(
        method &&
        method.enabled &&
        method.paygate_id &&
        method.secret &&
        method.currency
      );
    },
    paymentActiveIpay88(payments: RestaurantSchema["settings"]["payments"]) {
      const method = payments.ipay88;
      return !!(
        method &&
        method.enabled &&
        method.api_entry_url &&
        method.api_query_url &&
        method.merchant_key &&
        method.merchant_code &&
        method.currency
      );
    },
    paymentActiveBamboraAPAC(payments: RestaurantSchema["settings"]["payments"]) {
      const method = payments.bambora_apac;
      return !!(
        method &&
        method.enabled &&
        method.merchant_id &&
        method.api_username &&
        method.api_password &&
        method.currency
      );
    },
    paymentActivePesapal(payments: RestaurantSchema["settings"]["payments"]) {
      const method = payments.pesapal;
      return !!(
        method &&
        method.enabled &&
        method.consumer_secret &&
        method.consumer_key &&
        method.currency
      );
    },
    integrationFacebookPixelActive(r: RestaurantSafeSchema) {
      const i = r.settings.integrations;
      return !!(i && i.facebook_pixel && i.facebook_pixel.pixel_id);
    },
    integrationGoogleAnalyticsActive(r: RestaurantSafeSchema) {
      const i = r.settings.integrations;
      return !!(i && i.google_analytics && i.google_analytics.property_id);
    },
    integrationTookanActive(r: RestaurantSafeSchema) {
      const p = r.settings.services.delivery.providers;
      return !!(p && p.tookan && p.tookan.api_key && p.tookan.utc_offset);
    },
  },
};
